/**
 * Service for removing expired visitors from physical devices
 * Orchestrates device removal using deviceCommunicationService
 */

import md5 from 'md5';
import { ExpiredVisitor } from '../hooks/useExpiredVisitors';
import {
  deviceCommunicationService,
  DeviceInfo,
  DeviceOperations,
  CommunicationMode,
  DeviceOperationResult,
} from './deviceCommunication';
import { isElectron } from '../utils/platform';
import { logger } from './logger';

export interface DeviceRemovalResult {
  deviceId: string;
  deviceName: string;
  success: boolean;
  error?: string;
  latencyMs?: number;
  source?: 'local' | 'public' | 'cloud';
}

export interface DevicePresenceSummary {
  deviceId: string;
  deviceName: string;
  source: 'local' | 'public' | 'cloud';
  foundCount: number;
  status: 'done' | 'error';
  error?: string;
}

export interface PresenceCheckResult {
  /** visitorId → list of deviceIds where that visitor was found */
  visitorOnDevices: Map<string, string[]>;
  deviceSummary: DevicePresenceSummary[];
}

export class ExpiredVisitorDeviceService {
  /**
   * Fetch all devices for a customer
   */
  private async fetchCustomerDevices(customerId: string): Promise<DeviceInfo[]> {
    try {
      const { fetchWithAuth } = await import('./api');
      const { okJson } = await import('./httpError');
      const response = await fetchWithAuth(`/api/v1/iot-devices/customer/${customerId}`);
      // Unchecked, an error body fell through to `|| []`, so a failed lookup was logged as
      // "customer has no devices" and the expired-visitor removal silently did nothing.
      const data = await okJson<any>(response, 'Erro ao carregar os dispositivos do cliente');
      const devices = data?.devices || data?.data || [];

      return devices.map((d: any) => ({
        id: d.id,
        ip_address: d.ip_address,
        local_ip_address: d.local_ip_address,
        username: d.username || d.device_username || 'admin',
        password: d.password || d.device_password || '',
        vendor: d.vendor || 'hikvision',
        port: d.port,
      }));
    } catch (error) {
      logger.error('ExpiredVisitorDeviceService', 'Failed to fetch devices', error as Error);
      return [];
    }
  }

  /**
   * Remove a single visitor from all assigned devices
   */
  async removeVisitorFromDevices(
    customerId: string,
    visitor: ExpiredVisitor,
    onProgress?: (status: string) => void
  ): Promise<DeviceRemovalResult[]> {
    logger.info('ExpiredVisitorDeviceService', 'Removing visitor from devices', {
      visitorId: visitor.id,
      visitorName: visitor.name,
    });

    // 1. Fetch customer devices
    const allDevices = await this.fetchCustomerDevices(customerId);

    if (allDevices.length === 0) {
      logger.warn('ExpiredVisitorDeviceService', 'No devices found for customer', { customerId });
      return [];
    }

    // 2. Filter to devices where visitor is registered (if known)
    const relevantDevices = visitor.devices && visitor.devices.length > 0
      ? allDevices.filter(d => visitor.devices!.includes(d.id))
      : allDevices; // If no device list, try all devices

    logger.debug('ExpiredVisitorDeviceService', 'Relevant devices found', {
      total: allDevices.length,
      relevant: relevantDevices.length,
    });

    // 3. Remove from each device sequentially
    const results: DeviceRemovalResult[] = [];

    for (const device of relevantDevices) {
      onProgress?.(`Removing from ${device.id}...`);

      try {
        const result: DeviceOperationResult = await deviceCommunicationService.executeOperation(
          device,
          DeviceOperations.REMOVE_PERSON,
          {
            person_id: visitor.personId,
            devicePersonId: visitor.devicePersonId,
          },
          CommunicationMode.HYBRID // local IP → public IP → cloud
        );

        results.push({
          deviceId: device.id,
          deviceName: device.id,
          success: result.success,
          error: result.error,
          latencyMs: result.latencyMs,
          source: result.source,
        });

        logger.debug('ExpiredVisitorDeviceService', 'Device removal result', {
          deviceId: device.id,
          success: result.success,
          source: result.source,
        });
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        results.push({
          deviceId: device.id,
          deviceName: device.id,
          success: false,
          error: errorMessage,
        });

        logger.error('ExpiredVisitorDeviceService', 'Device removal failed', {
          deviceId: device.id,
          error: errorMessage,
        });
      }
    }

    return results;
  }

  /**
   * Remove multiple visitors in batch with progress tracking
   */
  async removeBatchFromDevices(
    customerId: string,
    visitors: ExpiredVisitor[],
    onProgress?: (current: number, total: number, visitorName: string) => void
  ): Promise<Map<string, DeviceRemovalResult[]>> {
    logger.info('ExpiredVisitorDeviceService', 'Starting batch removal', {
      count: visitors.length,
    });

    const results = new Map<string, DeviceRemovalResult[]>();

    for (let i = 0; i < visitors.length; i++) {
      const visitor = visitors[i];
      onProgress?.(i + 1, visitors.length, visitor.name);

      const removalResults = await this.removeVisitorFromDevices(
        customerId,
        visitor,
        (status) => logger.debug('ExpiredVisitorDeviceService', 'Device status', { status })
      );

      results.set(visitor.id, removalResults);
    }

    logger.info('ExpiredVisitorDeviceService', 'Batch removal completed', {
      totalVisitors: visitors.length,
    });

    return results;
  }

  /**
   * Check which expired visitors are actually registered on devices.
   * Only works in Electron (requires direct device access).
   * Returns a map of visitorId → deviceIds where they were found.
   */
  async checkPresenceOnDevices(
    customerId: string,
    visitors: ExpiredVisitor[],
    onDeviceProgress?: (checked: number, total: number, deviceId: string) => void
  ): Promise<PresenceCheckResult> {
    if (!isElectron()) {
      logger.warn('ExpiredVisitorDeviceService', 'Presence check requires Electron');
      return { visitorOnDevices: new Map(), deviceSummary: [] };
    }

    const allDevices = await this.fetchCustomerDevices(customerId);

    // Build lookup: every identifier a device could report this visitor under → visitor.id
    //
    // This used to key on visitor.devicePersonId ALONE — a role_metadata.device_person_id
    // that condo-manager never writes. The index was therefore empty for every real
    // visitor, so each device reported foundCount: 0 and the screen claimed nobody had
    // face recognition active, however many faces were actually enrolled.
    //
    // What the readers actually report (see parseDeviceUsers):
    //   ControlID → registration = person UUID    (syncControlIDPerson / EnrollPerson)
    //   Hikvision → employeeNo   = md5(personId)  (generateDevicePersonId)
    const devicePersonIdIndex = new Map<string, string>();
    for (const visitor of visitors) {
      const candidates = [
        visitor.personId,                              // ControlID registration
        visitor.personId ? md5(visitor.personId) : '', // Hikvision employeeNo
        visitor.devicePersonId || '',                  // explicit, when something did set it
      ];
      for (const candidate of candidates) {
        if (candidate) devicePersonIdIndex.set(candidate.toLowerCase(), visitor.id);
      }
    }

    const visitorOnDevices = new Map<string, string[]>();
    const deviceSummary: DevicePresenceSummary[] = [];

    for (let i = 0; i < allDevices.length; i++) {
      const device = allDevices[i];
      onDeviceProgress?.(i, allDevices.length, device.id);

      try {
        const result = await deviceCommunicationService.executeOperation(
          device,
          DeviceOperations.LOAD_PEOPLE,
          {}, // no filter → fetch all users
          CommunicationMode.HYBRID
        );

        if (result.success && result.data) {
          const empIds = this.parseDeviceUsers(device.vendor, result.data);
          let foundCount = 0;

          for (const empId of empIds) {
            const visitorId = devicePersonIdIndex.get(empId.toLowerCase());
            if (visitorId) {
              foundCount++;
              if (!visitorOnDevices.has(visitorId)) visitorOnDevices.set(visitorId, []);
              visitorOnDevices.get(visitorId)!.push(device.id);
            }
          }

          deviceSummary.push({
            deviceId: device.id,
            deviceName: device.id,
            source: result.source,
            foundCount,
            status: 'done',
          });
        } else {
          deviceSummary.push({
            deviceId: device.id,
            deviceName: device.id,
            source: result.source || 'local',
            foundCount: 0,
            status: 'error',
            error: result.error,
          });
        }
      } catch (err) {
        deviceSummary.push({
          deviceId: device.id,
          deviceName: device.id,
          source: 'local',
          foundCount: 0,
          status: 'error',
          error: err instanceof Error ? err.message : 'Unknown error',
        });
      }
    }

    onDeviceProgress?.(allDevices.length, allDevices.length, '');
    logger.info('ExpiredVisitorDeviceService', 'Presence check completed', {
      devicesChecked: allDevices.length,
      visitorsFound: visitorOnDevices.size,
    });

    return { visitorOnDevices, deviceSummary };
  }

  /** Extract employee/registration IDs from raw device LOAD_PEOPLE response */
  private parseDeviceUsers(vendor: string, data: any): string[] {
    if (vendor === 'hikvision') {
      const users: any[] = data?.UserInfoSearch?.UserInfo || [];
      return users.map(u => String(u.employeeNo || ''));
    }
    if (vendor === 'controlid') {
      const users: any[] = data?.users || [];
      return users.map(u => String(u.registration || u.id || ''));
    }
    // fallback / rfid
    const users: any[] = data?.users || data?.UserInfo || [];
    return users.map(u => String(u.employeeNo || u.registration || u.id || ''));
  }
}

// Singleton instance
export const expiredVisitorDeviceService = new ExpiredVisitorDeviceService();
